import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "@/App.css";
import { loadFreebieSkus } from "@/lib/freebieCalc";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import SellerInsights from "@/pages/SellerInsights";
import SellerRevenueRanking from "@/pages/SellerRevenueRanking";
import SellerDrillDown from "@/pages/SellerDrillDown";
import Sellers from "@/pages/Sellers";
import Products from "@/pages/Products";
import DiscountSlabs from "@/pages/DiscountSlabs";
import CreateQuotation from "@/pages/CreateQuotation";
import QuotationsLog from "@/pages/QuotationsLog";
import QuotationDetail from "@/pages/QuotationDetail";
import EditQuotation from "@/pages/EditQuotation";
import Drafts from "@/pages/Drafts";
import DispatchPanel from "@/pages/DispatchPanel";
import InventoryAllocation from "@/pages/InventoryAllocation";
import SlabIntelligence from "@/pages/SlabIntelligence";
import McfStockTransfers from "@/pages/McfStockTransfers";
import RevenueAnalytics from "@/pages/RevenueAnalytics";
import DiscountApprovals from "@/pages/DiscountApprovals";
import DiscountAnalytics from "@/pages/DiscountAnalytics";
import FreebieAnalytics from "@/pages/FreebieAnalytics";
import RemarksAnalytics from "@/pages/RemarksAnalytics";
import MonthlySalesAnalysis from "@/pages/MonthlySalesAnalysis";
import UsersManagement from "@/pages/UsersManagement";
import AccessPending from "@/pages/AccessPending";
import AccessDenied from "@/pages/AccessDenied";
import DashboardLayout from "@/components/DashboardLayout";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";

// Dynamically determine API URL at runtime - ALWAYS use current origin
// This ensures the app works on any domain (preview, production, localhost)
const getApiUrl = () => {
  return window.location.origin + '/api';
};

export const API = getApiUrl();

// Configure axios to use cookies for all requests
axios.defaults.withCredentials = true;

// Bearer-token fallback: in some production/cross-site contexts the SameSite=None
// session cookie is not sent back by the browser. We also store the session_token
// returned by /auth/session and send it as Authorization: Bearer so auth keeps
// working even when the cookie is dropped. (Backend honors the Authorization header.)
const storedSessionToken = localStorage.getItem('session_token');
if (storedSessionToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${storedSessionToken}`;
}

// Add response interceptor to handle 401 errors
// But NOT for auth/me calls (to prevent redirect loops on login page)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect to login for auth/me endpoint (used to check if already logged in)
    const isAuthCheck = error.config?.url?.includes('/auth/me');
    if (error.response?.status === 401 && !isAuthCheck) {
      localStorage.removeItem('user');
      localStorage.removeItem('session_token');
      delete axios.defaults.headers.common['Authorization'];
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Session token helpers (Bearer fallback for environments where the cookie is dropped)
export const setSessionToken = (token) => {
  if (token) {
    localStorage.setItem('session_token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
};
export const clearSessionToken = () => {
  localStorage.removeItem('session_token');
  delete axios.defaults.headers.common['Authorization'];
};

// Dummy function for backward compatibility with existing code
// The actual auth is handled via cookies now
export const getAuthToken = () => "";
export const setAuthToken = () => {};
export const removeAuthToken = () => {};

// Helper functions for user management
export const getUser = () => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};

export const setUser = (user) => localStorage.setItem('user', JSON.stringify(user));
export const removeUser = () => {
  localStorage.removeItem('user');
  clearSessionToken();
};

export const isAdmin = () => {
  const user = getUser();
  return user?.role === 'admin';
};

export const hasModule = (module) => {
  const user = getUser();
  if (!user) return false;
  if (user.role === 'admin') return true;
  return (user.permissions || []).includes(module);
};

export const isAccessActive = () => {
  const user = getUser();
  if (!user) return false;
  if (user.role === 'admin') return true;
  return (user.status || 'active') === 'active';
};

// Protected route wrapper
function ProtectedRoute({ children }) {
  const location = useLocation();
  // Start with null (unknown auth state) instead of immediately assuming not authenticated
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userState, setUserState] = useState(null);

  const checkAuth = useCallback(async () => {
    // Don't check auth if there's a session_id being processed
    if (window.location.hash?.includes('session_id=')) {
      console.log("[ProtectedRoute] session_id in hash, skipping auth check");
      return;
    }
    
    // If user data was passed from AuthCallback via state, use it
    if (location.state?.user) {
      console.log("[ProtectedRoute] User passed via state, using it");
      localStorage.setItem('user', JSON.stringify(location.state.user));
      setUserState(location.state.user);
      setIsAuthenticated(true);
      setIsLoading(false);
      return;
    }
    
    console.log("[ProtectedRoute] Checking authentication via /auth/me...");
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true  // CRITICAL: Ensure cookies are sent
      });
      console.log("[ProtectedRoute] Auth successful:", response.data?.email, "status:", response.data?.status);
      localStorage.setItem('user', JSON.stringify(response.data));
      setUserState(response.data);
      setIsAuthenticated(true);
      // Hydrate freebie SKU whitelist from backend (admin-managed). Fire and forget.
      loadFreebieSkus(API);
    } catch (error) {
      console.log("[ProtectedRoute] Auth failed:", error.response?.status, error.response?.data);
      localStorage.removeItem('user');
      localStorage.removeItem('session_token');
      delete axios.defaults.headers.common['Authorization'];
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, [location.state]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log("[ProtectedRoute] Not authenticated, redirecting to login");
    return <Navigate to="/login" replace />;
  }

  // Authenticated but pending admin approval — block all routes and show
  // the "Access Pending Approval" screen instead.
  const effectiveUser = userState || getUser();
  if (effectiveUser && effectiveUser.role !== 'admin' && (effectiveUser.status || 'active') !== 'active') {
    return <AccessPending user={effectiveUser} />;
  }

  return children;
}

// Per-module route wrapper. Admins always pass; otherwise the user must have the
// `module` key in `permissions`. Pending users never reach here (caught by ProtectedRoute).
function ModuleRoute({ module, children }) {
  const user = getUser();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'admin') return children;
  if ((user.permissions || []).includes(module)) return children;
  return <AccessDenied module={module} />;
}

// Admin route wrapper
function AdminRoute({ children }) {
  const user = getUser();
  const isUserAdmin = user?.role === 'admin';
  
  if (!isUserAdmin) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

// Main App Router
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id synchronously
  // Use window.location.hash directly for immediate check (React Router's location might lag)
  const hash = window.location.hash || location.hash || '';
  
  if (hash.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="dashboard"><Dashboard /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/seller-insights" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="seller_insights"><SellerInsights /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/seller-revenue-ranking" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="seller_insights"><SellerRevenueRanking /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/seller-drill-down/:sellerId" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="seller_insights"><SellerDrillDown /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/sellers" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="sellers"><Sellers /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/products" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="products"><Products /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/discount-slabs" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="products"><DiscountSlabs /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/quotations/create" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="quotations"><CreateQuotation /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/quotations-log" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="quotations"><QuotationsLog /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/quotations/:quotationId" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="quotations"><QuotationDetail /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/quotations/:quotationId/edit" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="quotations"><EditQuotation /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/drafts" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="quotations"><Drafts /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/discount-approvals" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="discount_approvals"><DiscountApprovals /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/dispatch" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="dispatch"><DispatchPanel /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/inventory-allocation" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="inventory_allocation"><InventoryAllocation /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/mcf-stock-transfers" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="mcf_stock_transfers"><McfStockTransfers /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/slab-intelligence" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="slab_intelligence"><SlabIntelligence /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/revenue-analytics" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="revenue_analytics"><RevenueAnalytics /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/discount-analytics" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="discount_analytics"><DiscountAnalytics /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/freebie-analytics" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="freebie_analytics"><FreebieAnalytics /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/remarks-analytics" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="remarks_analytics"><RemarksAnalytics /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/monthly-sales-analysis" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="monthly_sales_analysis"><MonthlySalesAnalysis /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ModuleRoute module="control_panel"><UsersManagement /></ModuleRoute>
          </DashboardLayout>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-right" />
      </ErrorBoundary>
    </div>
  );
}

export default App;
